import { useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useNavigate } from 'react-router-dom';

import Button from '@/components/Button';
import Checkbox from '@/components/Checkbox';
import DownloadModal from '@/components/DownloadModal';
import { useIsTablet } from '@/hooks';
import useFormatDateTime from '@/hooks/useFormatDateTime';
import { useIsCollaborator, useSubscribed, useUser } from '@/hooks/user';
import Edit from '@/icons/edit.svg';
import Folder from '@/icons/folder.svg';
import routes from '@/routes';
import { setTriggerAction } from '@/store/editor';
import { setIsModalRateUsOpen } from '@/store/myDocuments';
import {
	setIsModalUpgradePlanOpen,
	setModalUpgradePlanScope
} from '@/store/profile';
import { cn, formatFileSize } from '@/utils/index';

import { DropdownMenuCell } from './DropdownMenuCell';
import { usePendingDownload } from './hooks';

export function SelectColumn({ table }) {
	return (
		<div className="flex items-center">
			<Checkbox
				checked={
					table.getIsAllPageRowsSelected() ||
					(table.getIsSomePageRowsSelected() && 'indeterminate')
				}
				onCheckedChange={value => table.toggleAllPageRowsSelected(!!value)}
				aria-label="Select all"
			/>
		</div>
	);
}

export function SelectCell({ row }) {
	return (
		<div className="flex items-center">
			<Checkbox
				checked={row.getIsSelected()}
				onCheckedChange={value => row.toggleSelected(!!value)}
				aria-label="Select row"
			/>
		</div>
	);
}

export function NameCell({ row }) {
	const {
		folderName,
		metadata: { originalFileName }
	} = row.original;

	return (
		<div className="flex items-center gap-4">
			<div className="flex h-12 w-12 items-center justify-center rounded bg-red-background">
				<div className="text-sm font-semibold leading-normal text-red">
					{row.original.metadata.type.toUpperCase()}
				</div>
			</div>
			<div className="flex flex-col gap-2">
				<span className="truncate text-xs font-semibold leading-5 md:max-w-36 lg:max-w-60 xl:max-w-80">
					{originalFileName}
				</span>
				{Boolean(folderName) && (
					<div className="flex w-fit items-center justify-start gap-1 rounded-md bg-grey-100 p-1 text-grey-500">
						<Folder aria-hidden className="size-4" />
						<span className="mt-[1px] truncate text-[0.625rem] leading-4 md:max-w-36 lg:max-w-60 xl:max-w-full">
							{folderName}
						</span>
					</div>
				)}
			</div>
		</div>
	);
}

export function UpdatedAtCell({ row }) {
	const formatDateTime = useFormatDateTime();

	return (
		<div className="flex min-w-24 items-center">
			<span className="text-xs leading-5 text-grey-500">
				{formatDateTime(row.original.createdAt)}
			</span>
		</div>
	);
}

export function SizeCell({ row }) {
	return (
		<div className="flex min-w-10 items-center">
			<span className="text-xs leading-5 text-grey-500">
				{formatFileSize(row.original.metadata.fileSize)}
			</span>
		</div>
	);
}

export function ButtonsCell({ row }) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const isCollaborator = useIsCollaborator();
	const subscribed = useSubscribed();
	const selectedTransactions = useMemo(() => [row.original], [row]);
	const isTablet = useIsTablet();
	const triggerDownload = useSelector(
		state => state.editor.triggerAction === 'download'
	);
	const rateUsOpen = useSelector(state => state.myDocuments.isModalRateUsOpen);
	const paymentBreakdownModalOpen = useSelector(
		({ checkout }) => checkout?.isPaymentBreakdownModalOpen
	);

	const user = useUser();

	const { defaultDownloadOpen, clearPending } = usePendingDownload({
		id: row.original.id
	});

	const [isDownloadDialogOpen, setIsDownloadDialogOpen] = useState(false);

	const handleOpenChange = nextValue => {
		if (!subscribed) {
			dispatch(setModalUpgradePlanScope('download'));
			dispatch(setIsModalUpgradePlanOpen(true));
			return;
		}

		if (!nextValue) {
			clearPending();
		}

		setIsDownloadDialogOpen(nextValue);
	};

	useEffect(() => {
		if (subscribed && defaultDownloadOpen && !paymentBreakdownModalOpen) {
			if (user?.canRequestReview) {
				dispatch(setIsModalRateUsOpen(true));
			} else if (!rateUsOpen) {
				setIsDownloadDialogOpen(true);
			}
		}
	}, [subscribed, defaultDownloadOpen, paymentBreakdownModalOpen]);

	useEffect(() => {
		if (triggerDownload) {
			setIsDownloadDialogOpen(true);
			dispatch(setTriggerAction(''));
		}
	}, [triggerDownload]);

	const renderEditButton = () => (
		<Button
			className={cn(isTablet && 'h-8 w-8 p-0')}
			size="sm"
			startIcon={!isTablet ? <Edit className="text-black" /> : undefined}
			variant="outline"
			onClick={() =>
				navigate(generatePath(routes.editor, { id: row.original.id }))
			}
		>
			{isTablet ? <Edit className="text-black" /> : t('common.edit')}
		</Button>
	);

	return (
		<div className="flex w-full shrink-0 items-center justify-end gap-2">
			{!isCollaborator && renderEditButton()}

			<DownloadModal
				open={isDownloadDialogOpen}
				onOpenChange={handleOpenChange}
				selectedTransactions={selectedTransactions}
				withTrigger
				isTablet={isTablet}
			/>

			{isTablet && <DropdownMenuCell row={row} isTablet={isTablet} />}
		</div>
	);
}
